<template>
  <label
    for="imgInput"
    class="account__avatar-loader__input"
  >
    {{ $t('account.AccountAvatarLoader.label') }}
  </label>
  <input
    id="imgInput"
    ref="$imgInput"
    type="file"
    class="hidden"
    accept=".jpg, .jpeg, .png, .webp"
    @change="openCropPopup"
  >

  <UIModal
    :model-value="modalIsShow"
    size="mw"
    @close="closeModal"
  >
    <div class="account__avatar-loader__popup-title">
      {{ $t('account.AccountAvatarLoader.popupTitle') }}
    </div>
    <VueCropper
      ref="$cropper"
      :src="userImg"
      :modal="false"
      :aspect-ratio="1"
      :min-crop-box-width="145"
      :min-crop-box-height="145"
    />
    <div class="account__avatar-loader__popup-actions">
      <div
        v-if="error"
        class="account__avatar-loader__popup-error"
      >
        {{ $t('account.AccountAvatarLoader.popupError') }}
      </div>

      <UIButton
        type="black"
        size="m"
        class="w-56 rounded"
        :loading="loading"
        @click="uploadAvatar"
      >
        {{ $t('account.AccountAvatarLoader.button') }}
      </UIButton>
    </div>
  </UIModal>
</template>

<script lang="ts" setup>
import VueCropper from 'vue-cropperjs';
import UIModal from '~/components/ui/UIModal.vue';
import UIButton from '~/components/ui/UIButton.vue';
import 'cropperjs/dist/cropper.css';
import { img2Base64 } from '~/utils/files';

import { useApi } from '~/restAPI';
import { Account } from '~/restAPI/Account';

const { getAccount } = useAccountStore();

const $cropper = ref();
const $imgInput = ref();
const userImg = ref();
const modalIsShow = ref(false);
const error = ref(false);
const loading = ref(false);

const { createApiInstance } = useApi();
const accountApi = createApiInstance(Account);

const file = ref<File>();
const imgName = ref('');

const {
  data: uploadUserAvatarData,
  execute: uploadUserAvatar,
  error: uploadUserAvatarError,
} = useLazyAsyncData(() => accountApi.uploadImageUsingPost({ image: file.value! })
  .then((res) => res?.data.data), { immediate: false });

function openCropPopup(event: Event) {
  if (!event.target?.files.length) return;

  img2Base64(event.target.files[0]).then(({ base64, img }) => {
    imgName.value = img.name;

    userImg.value = base64;
    modalIsShow.value = true;

    nextTick(() => $cropper.value.replace(base64));
  });
}

function closeModal() {
  modalIsShow.value = false;
  userImg.value = undefined;
  error.value = false;
  $imgInput.value.value = '';
  $cropper.value.destroy();
}

async function uploadAvatar() {
  $cropper.value
    .getCroppedCanvas({ width: 300, height: 300, fillColor: '#fff' })
    .toBlob(async (blob: Blob) => {
      file.value = new File([blob], imgName.value);

      loading.value = true;

      await uploadUserAvatar();

      if (uploadUserAvatarError.value) {
        error.value = true;
        loading.value = false;
        return;
      }

      if (uploadUserAvatarData.value) {
        await getAccount();
        closeModal();
      }

      loading.value = false;
    }, 'image/jpeg');
}
</script>

<style scoped lang="scss">
@tailwind components;

@layer components {
  .account__avatar-loader {
    &__input {
      @apply flex items-center h-8 text-xs text-[#202020] border-zinc-200 hover:bg-neutral-200 ml-4 font-semibold border rounded-sm px-10 cursor-pointer rounded;
    }

    &__popup {
      &-title {
        @apply text-black text-2xl mb-6;
      }

      &-actions {
        @apply flex flex-col items-center justify-center mt-6;
      }

      &-error {
        @apply text-red-600 text-sm mb-3;
      }
    }
  }
}
</style>
